<template>
  <div class="create-screener-modal-root">
  <AppModal
    :value="value"
    :title="modalTitle"
    :width="534"
    content-class="create-screener-modal"
    @input="onModalInput"
    @close="onClose"
  >
    <div class="create-screener-modal__body">
      <div v-if="isLoadingDetail" class="create-screener-modal__loading">
        <v-skeleton-loader type="list-item-two-line" />
        <v-skeleton-loader type="list-item-two-line" />
        <v-skeleton-loader type="list-item-three-line" />
      </div>

      <template v-else>
      <label class="create-screener-modal__field">
        <span class="create-screener-modal__label">Screener Name</span>
        <input v-model="screenerName" type="text" class="create-screener-modal__input"
          placeholder="Enter screener name" />
      </label>

      <div class="create-screener-modal__field">
        <span class="create-screener-modal__label">Project</span>
        <AppSelect
          name="create-screener-project"
          :value="selectedProjectId || ''"
          placeholder="Select"
          empty-text="No projects found"
          :options="projectSelectOptions"
          :is-loading-more="isLoadingProjects"
          :disabled="isEditMode"
          searchable
          search-placeholder="Search projects"
          input-class-name="create-screener-modal__project-select"
          @input="onSelectProject"
          @search="onProjectSearch"
        />
      </div>

      <div class="create-screener-modal__field">
        <span class="create-screener-modal__label">Qualify Method</span>
        <div class="create-screener-modal__method-cards">
          <button
            type="button"
            class="create-screener-modal__method-card"
            :class="{
              'create-screener-modal__method-card--selected': qualifyMethod === 'automatic',
              'create-screener-modal__method-card--disabled': isEditMode,
            }"
            :disabled="isEditMode"
            @click="onSelectQualifyMethod('automatic')"
          >
            <span class="create-screener-modal__radio" :class="{
              'create-screener-modal__radio--selected': qualifyMethod === 'automatic',
            }" aria-hidden="true">
              <span class="create-screener-modal__radio-dot"></span>
            </span>
            <span class="create-screener-modal__method-copy">
              <span class="create-screener-modal__method-title">Automatic</span>
              <span class="create-screener-modal__method-desc">
                System qualifies participants instantly based on rules. Supports Yes/No,
                Single choice, and Multiple choice questions only.
              </span>
            </span>
          </button>

          <button
            type="button"
            class="create-screener-modal__method-card"
            :class="{
              'create-screener-modal__method-card--selected': qualifyMethod === 'manual',
              'create-screener-modal__method-card--disabled': isEditMode,
            }"
            :disabled="isEditMode"
            @click="onSelectQualifyMethod('manual')"
          >
            <span class="create-screener-modal__radio" :class="{
              'create-screener-modal__radio--selected': qualifyMethod === 'manual',
            }" aria-hidden="true">
              <span class="create-screener-modal__radio-dot"></span>
            </span>
            <span class="create-screener-modal__method-copy">
              <span class="create-screener-modal__method-title">Manual</span>
              <span class="create-screener-modal__method-desc">
                You review and qualify each response yourself. Also supports Short text
                and Long text questions.
              </span>
            </span>
          </button>
        </div>
      </div>
      </template>
    </div>

    <template v-slot:footer>
      <div class="create-screener-modal__footer">
        <button
          type="button"
          class="create-screener-modal__btn create-screener-modal__btn--outline"
          :disabled="isSubmitting"
          @click="onClose"
        >
          <span>Cancel</span>
        </button>
        <button
          type="button"
          class="create-screener-modal__btn create-screener-modal__btn--primary"
          :disabled="!canCreate || isSubmitting"
          @click="onCreate"
        >
          <span>{{ primaryButtonLabel }}</span>
        </button>
      </div>
    </template>
  </AppModal>

  <AppToast
    v-model="toast.show"
    :variant="toast.variant"
    :message="toast.message"
  />
  </div>
</template>

<script>
import AppModal from '@/components/appModal/AppModal.vue';
import AppToast from '@/components/appToast/AppToast.vue';
import AppSelect from '@/components/appForm/AppSelect.vue';
import ProjectsAPI from '@/api/projects/projects';
import ScreenersAPI from '@/api/screeners/screeners';
import { getApiErrorMessage } from '@/utils/apiError';

export default {
  name: 'CreateScreenerModal',
  components: { AppModal, AppToast, AppSelect },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit'].includes(value),
    },
    initialValues: {
      type: Object,
      default: () => ({}),
    },
    projects: {
      type: Array,
      default: () => [],
    },
    defaultProjectId: {
      type: String,
      default: null,
    },
    /**
     * 'screener' for project-level, 'opt_in' for organization-level.
     * Falls back to route context when not provided.
     */
    formType: {
      type: String,
      default: '',
      validator: (value) => !value || ['screener', 'opt_in'].includes(value),
    },
  },
  data() {
    return {
      screenerName: '',
      selectedProjectId: null,
      selectedProjectName: '',
      qualifyMethod: 'automatic',
      fetchedProjects: [],
      isLoadingProjects: false,
      projectSearch: '',
      isLoadingDetail: false,
      isSubmitting: false,
      toast: {
        show: false,
        variant: 'success',
        message: '',
      },
    };
  },
  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Screener' : 'Create Screener';
    },
    projectOptions() {
      return this.projects.length ? this.projects : this.fetchedProjects;
    },
    projectSelectOptions() {
      return this.projectOptions.map((project) => ({
        value: project.id,
        label: project.emoji ? `${project.emoji} ${project.name}` : project.name,
      }));
    },
    canCreate() {
      if (this.isEditMode) {
        return Boolean(this.screenerName.trim()) && !this.isLoadingDetail;
      }
      return Boolean(
        this.screenerName.trim() && this.selectedProjectId && this.qualifyMethod,
      );
    },
    primaryButtonLabel() {
      if (this.isEditMode) {
        return this.isSubmitting ? 'Saving...' : 'Save';
      }
      if (this.isSubmitting) return 'Creating...';
      return 'Create & Add Questions';
    },
    resolvedFormType() {
      if (this.formType === 'screener' || this.formType === 'opt_in') {
        return this.formType;
      }
      return this.$route.params.projectId ? 'screener' : 'opt_in';
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.onModalOpen();
      } else {
        this.resetState();
      }
    },
  },
  methods: {
    showToast(message, variant = 'success') {
      this.toast = { show: true, variant, message };
    },
    async onModalOpen() {
      if (this.isEditMode) {
        await this.loadEditDetail();
        return;
      }
      this.hydrateFromInitial();
      this.loadProjects();
    },
    async loadEditDetail() {
      const screenerId = this.initialValues.id;
      if (!screenerId) {
        this.hydrateFromInitial();
        return;
      }

      this.isLoadingDetail = true;
      this.hydrateFromInitial();

      try {
        const res = await ScreenersAPI.getScreenerDetail({
          screenerId,
          projectId: this.$route.params.projectId || null,
        });

        if (res.status === 'success') {
          const screener = res.data?.screener || res.data || {};
          this.screenerName = screener.name || this.initialValues.name || '';
          this.selectedProjectId =
            screener.projectId || this.initialValues.projectId || null;
          this.selectedProjectName =
            screener.projectName || this.initialValues.projectName || '';
          this.qualifyMethod =
            screener.qualifyMethod ||
            this.initialValues.qualifyMethod ||
            'automatic';

          if (
            this.selectedProjectId &&
            this.selectedProjectName &&
            !this.projectOptions.some((p) => p.id === this.selectedProjectId)
          ) {
            this.fetchedProjects = [
              {
                id: this.selectedProjectId,
                name: this.selectedProjectName,
                emoji: screener.projectEmoji || '',
              },
            ];
          }
        } else {
          this.showToast(
            getApiErrorMessage(res.error, 'Failed to load screener'),
            'error',
          );
          this.$store.dispatch('sendAlert', {
            workflow: 'get_screener_detail',
            error: res.error,
          });
        }
      } catch (error) {
        this.showToast(
          getApiErrorMessage(error, 'Failed to load screener'),
          'error',
        );
        this.$store.dispatch('sendAlert', {
          workflow: 'get_screener_detail',
          error,
        });
      } finally {
        this.isLoadingDetail = false;
      }
    },
    async loadProjects() {
      if (this.projects.length) {
        this.fetchedProjects = [];
        this.isLoadingProjects = false;
        this.applyDefaultProjectSelection();
        return;
      }

      this.isLoadingProjects = true;

      try {
        const projectsResponse = await ProjectsAPI.getProjects(
          this.projectSearch || null,
          null,
          null,
          null,
          'GET_FILTERS_WORKSPACE_PROJECTS',
        );
        const fetchedProjects = projectsResponse?.data?.projects || [];

        const allProjects = [];
        const sampleAndUnassignedProjects = [];
        let defaultEncountered = false;

        fetchedProjects.forEach((project) => {
          const isDefault =
            Object.prototype.hasOwnProperty.call(project, 'default') &&
            project.default === true;
          if (!defaultEncountered && isDefault) {
            defaultEncountered = true;
          }

          if (defaultEncountered) {
            sampleAndUnassignedProjects.push(project);
          } else {
            allProjects.push(project);
          }
        });

        this.fetchedProjects = [...allProjects, ...sampleAndUnassignedProjects];
        this.applyDefaultProjectSelection();
      } catch (error) {
        this.fetchedProjects = [];
        this.showToast('Failed to load projects', 'error');
        this.$store.dispatch('sendAlert', {
          workflow: 'list_screener_projects',
          error,
        });
      } finally {
        this.isLoadingProjects = false;
      }
    },
    applyDefaultProjectSelection() {
      if (this.isEditMode) return;

      const projectId =
        this.selectedProjectId ||
        this.defaultProjectId ||
        (this.resolvedFormType === 'screener'
          ? this.$route.params.projectId || null
          : null);

      if (!projectId) return;

      this.selectedProjectId = projectId;
      const match = this.projectOptions.find((project) => project.id === projectId);
      if (match?.name) {
        this.selectedProjectName = match.name;
      }
    },
    hydrateFromInitial() {
      if (!this.isEditMode) {
        this.resetState();
        this.applyDefaultProjectSelection();
        return;
      }
      this.screenerName = this.initialValues.name || '';
      this.selectedProjectId = this.initialValues.projectId || null;
      this.selectedProjectName = this.initialValues.projectName || '';
      this.qualifyMethod = this.initialValues.qualifyMethod || 'automatic';
    },
    resetState() {
      this.screenerName = '';
      this.selectedProjectId = null;
      this.selectedProjectName = '';
      this.qualifyMethod = 'automatic';
      this.isLoadingDetail = false;
      this.isSubmitting = false;
      this.projectSearch = '';
    },
    onProjectSearch(query) {
      this.projectSearch = query || '';
      this.loadProjects();
    },
    onModalInput(isOpen) {
      this.$emit('input', isOpen);
    },
    onClose() {
      if (this.isSubmitting || this.isLoadingDetail) return;
      this.resetState();
      this.$emit('input', false);
    },
    onSelectProject(projectId) {
      if (this.isEditMode) return;
      this.selectedProjectId = projectId;
    },
    onSelectQualifyMethod(method) {
      if (this.isEditMode) return;
      this.qualifyMethod = method;
    },
    async onCreate() {
      if (!this.canCreate || this.isSubmitting) return;

      const project = this.projectOptions.find(
        (p) => p.id === this.selectedProjectId,
      );
      const payload = {
        id: this.initialValues.id || null,
        name: this.screenerName.trim(),
        projectId: this.selectedProjectId,
        projectName: project?.name || this.selectedProjectName || '',
        qualifyMethod: this.qualifyMethod,
        type: this.resolvedFormType,
      };

      this.isSubmitting = true;

      try {
        if (this.isEditMode) {
          if (!payload.id) {
            this.showToast('Missing screener ID', 'error');
            return;
          }

          const res = await ScreenersAPI.updateScreener({
            screenerId: payload.id,
            name: payload.name,
            projectId:
              this.$route.params.projectId || payload.projectId || null,
          });

          if (res.status === 'success') {
            const entity =
              res.data?.screener || res.data?.segment || res.data || {};
            this.$emit('save', {
              ...payload,
              name: entity.name || payload.name,
              projectId: entity.projectId || payload.projectId,
              projectName: entity.projectName || payload.projectName,
              qualifyMethod: entity.qualifyMethod || payload.qualifyMethod,
            });
            this.resetState();
            this.$emit('input', false);
            return;
          }

          this.showToast(
            getApiErrorMessage(res.error, 'Failed to update screener'),
            'error',
          );
          this.$store.dispatch('sendAlert', {
            workflow: 'update_screener',
            error: res.error,
          });
          return;
        }

        const res = await ScreenersAPI.createScreener({
          name: payload.name,
          projectId: payload.projectId,
          qualifyMethod: payload.qualifyMethod,
          type: payload.type,
        });

        if (res.status === 'success') {
          const entity =
            res.data?.segment || res.data?.screener || res.data || {};
          const screenerId = entity.id;

          if (!screenerId) {
            this.showToast('Screener created but ID is missing', 'error');
            this.$store.dispatch('sendAlert', {
              workflow: 'create_screener',
              error: new Error('Create response missing segment/screener id'),
            });
            return;
          }

          this.$emit('create', {
            id: screenerId,
            name: entity.name || payload.name,
            projectId: entity.projectId || payload.projectId,
            projectName: entity.projectName || payload.projectName,
            projectEmoji: entity.projectEmoji || project?.emoji || '',
            qualifyMethod: entity.qualifyMethod || payload.qualifyMethod,
            type: entity.type || payload.type,
            questions: entity.questions || [],
            status: entity.status,
          });
          this.resetState();
          this.$emit('input', false);
        } else {
          this.showToast(
            getApiErrorMessage(res.error, 'Failed to create screener'),
            'error',
          );
          this.$store.dispatch('sendAlert', {
            workflow: 'create_screener',
            error: res.error,
          });
        }
      } catch (error) {
        this.showToast(
          getApiErrorMessage(
            error,
            this.isEditMode
              ? 'Failed to update screener'
              : 'Failed to create screener',
          ),
          'error',
        );
        this.$store.dispatch('sendAlert', {
          workflow: this.isEditMode ? 'update_screener' : 'create_screener',
          error,
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.create-screener-modal__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.create-screener-modal__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.create-screener-modal__label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: #4c4c4c;
}

.create-screener-modal__input {
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #ffffff;
  box-sizing: border-box;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  color: #4c4c4c;
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.create-screener-modal__input:focus {
  border-color: #5b7edb;
}

.create-screener-modal__method-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.create-screener-modal__method-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
}

.create-screener-modal__method-card:hover {
  background: #fafbff;
}

.create-screener-modal__method-card--selected {
  border-color: #5b7edb;
  background: #fafbff;
}

.create-screener-modal__method-card--disabled {
  cursor: not-allowed;
  opacity: 0.85;
  background: #f8f8f8;
}

.create-screener-modal__method-card--disabled.create-screener-modal__method-card--selected {
  background: #f0f4ff;
}

.create-screener-modal__loading {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.create-screener-modal__radio {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 1.5px solid #cccccc;
  border-radius: 50%;
  flex-shrink: 0;
  box-sizing: border-box;
}

.create-screener-modal__radio-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
}

.create-screener-modal__radio--selected {
  border-color: #5b7edb;
}

.create-screener-modal__radio--selected .create-screener-modal__radio-dot {
  background: #5b7edb;
}

.create-screener-modal__method-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.create-screener-modal__method-title {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: #4c4c4c;
}

.create-screener-modal__method-desc {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  color: #999999;
}

.create-screener-modal__footer {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.create-screener-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 48px;
  padding: 0 24px;
  border-radius: 44px;
  cursor: pointer;
  white-space: nowrap;
}

.create-screener-modal__btn span {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: inherit;
}

.create-screener-modal__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.create-screener-modal__btn--outline {
  border: 1px solid #5b7edb;
  background: #ffffff;
  color: #5b7edb;
}

.create-screener-modal__btn--primary {
  border: none;
  background: #5b7edb;
  color: #ffffff;
}

.create-screener-modal__btn--primary:hover:not(:disabled) {
  background: #4a6bc7;
}
</style>
